import React, { useCallback, useEffect } from 'react';
import { MeasuredWallpaperData } from '../../Types/ProductPageModel';
import useWindowSize from '../../Hooks/useWindowSize';

interface MeasuredWallpaperRulersProps {
  measuredWallpaper: MeasuredWallpaperData;
  wallWidth: number;
  wallHeight: number;
  selectionRef: React.MutableRefObject<HTMLDivElement | null>;
  showRuler: boolean;
  showVerticalLines: boolean;
  showHorizontalLines: boolean;
}

const MeasuredWallpaperRulers = ({
  showRuler,
  showVerticalLines,
  showHorizontalLines,
  measuredWallpaper,
  wallWidth,
  wallHeight,
  selectionRef,
}: MeasuredWallpaperRulersProps) => {
  const [windowWidth, windowHeight] = useWindowSize();

  const drawLinesOnSelection = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      const gapInCm = 50;
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'indianred';

      const selectionRect = selectionRef.current?.getBoundingClientRect();
      const selectionWidth = selectionRect?.width ?? 0;
      const cmToPxWidth = selectionWidth / wallWidth;
      const selectionHeight = ctx.canvas.height;
      const cmToPxHeight = selectionHeight / wallHeight;

      const numHorizontalLines = wallWidth / gapInCm;
      const numVerticalLines = wallHeight / gapInCm;
      const strokeGapHorizontal = gapInCm * cmToPxWidth;
      const strokeGapVertical = gapInCm * cmToPxHeight;

      if (showVerticalLines) {
        // X-axis, i.e. vertical lines
        for (
          let j = 0, i = strokeGapHorizontal;
          j < numHorizontalLines - 1;
          j++
        ) {
          ctx.beginPath();
          ctx.setLineDash([5, 3]);
          ctx.moveTo(i, 0);
          ctx.lineTo(i, ctx.canvas.height);
          ctx.stroke();
          i += strokeGapHorizontal;
        }
      }

      if (showHorizontalLines) {
        // Y-axis, i.e. horizontal lines
        for (
          let k = 0, p = strokeGapVertical;
          k < numVerticalLines - 1;
          k++
        ) {
          ctx.beginPath();
          ctx.setLineDash([5, 3]);
          ctx.moveTo(0, ctx.canvas.height - p);
          ctx.lineTo(ctx.canvas.width, ctx.canvas.height - p);
          ctx.stroke();
          p += strokeGapVertical;
        }
      }
    },
    [
      selectionRef,
      wallWidth,
      wallHeight,
      showHorizontalLines,
      showVerticalLines,
    ]
  );

  const drawRuler = useCallback(
    (ctx: CanvasRenderingContext2D, isHorizontal: boolean) => {
      const tickStepInCm = 10;
      const tickLabelEachCm = 100;
      const labelPerTick = tickLabelEachCm / tickStepInCm;
      const rulerHeight = isHorizontal
        ? ctx.canvas.height
        : ctx.canvas.width;
      const cmToPx = isHorizontal
        ? ctx.canvas.width / wallWidth
        : ctx.canvas.height / wallHeight;
      const numTicks = Math.floor(
        isHorizontal ? wallWidth : wallHeight / tickStepInCm
      );
      const tickGapPx = tickStepInCm * cmToPx;

      for (let i = 0, pos = 0; i <= numTicks; i++) {
        const isFullTick = i % labelPerTick === 0;
        const isHalfTick = i % (labelPerTick / 2) === 0;
        let tickLength = rulerHeight / 2 - 2;

        ctx.lineWidth = 1;
        if (isFullTick) {
          tickLength += 12;
          ctx.lineWidth = 2;
        } else if (isHalfTick) {
          tickLength += 8;
        }

        ctx.beginPath();
        if (isHorizontal) {
          ctx.moveTo(pos, 0);
          ctx.lineTo(pos, tickLength);
        } else {
          ctx.moveTo(0, ctx.canvas.height - pos);
          ctx.lineTo(tickLength, ctx.canvas.height - pos);
        }
        ctx.stroke();

        // Draw label on each "full" thick, except for the first and last tick
        if (isFullTick && i !== 0 && i != numTicks) {
          ctx.font = `${wallWidth > 1000 ? 8 : 10}px AKZIDENZ, sans-serif`;
          const text = `${i * 10} cm`;
          const textMetrics = ctx.measureText(text);
          const textWidth = textMetrics.width;
          const textHeight =
            textMetrics.actualBoundingBoxAscent +
            textMetrics.actualBoundingBoxDescent;
          if (isHorizontal) {
            ctx.fillText(
              text,
              pos - textWidth / 2,
              tickLength + textHeight + 4
            );
          } else {
            // Save current state of the context
            ctx.save();
            // Set origin to where we want to draw the text
            ctx.translate(tickLength + 4, ctx.canvas.height - pos);
            ctx.textAlign = 'center';
            // Rotate and draw text
            ctx.rotate(Math.PI / 2);
            ctx.fillText(text, 0, 0);
            // Restore context to saved state (non-rotated)
            ctx.restore();
          }
        }

        pos += tickGapPx;
      }
    },
    [wallWidth, wallHeight, windowWidth, windowHeight]
  );

  useEffect(() => {
    const canvas = document.querySelector('#rulers-canvas') as
      | HTMLCanvasElement
      | undefined;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.canvas.width = ctx.canvas.offsetWidth;
    ctx.canvas.height = ctx.canvas.offsetHeight;
    drawLinesOnSelection(ctx);
  }, [drawLinesOnSelection]);

  useEffect(() => {
    const rightRulerCanvas = document.querySelector(
      '#right-rulers-canvas'
    ) as HTMLCanvasElement | undefined;
    const bottomRulerCanvas = document.querySelector(
      '#bottom-rulers-canvas'
    ) as HTMLCanvasElement | undefined;
    const rulerSize = 48;

    if (rightRulerCanvas) {
      const ctx = rightRulerCanvas.getContext('2d');
      if (!ctx) return;

      ctx.canvas.width = rulerSize;
      ctx.canvas.height = ctx.canvas.offsetHeight;
      ctx.canvas.style.right = `-${rulerSize}px`;
      ctx.canvas.style.top = `0`;

      if (showRuler) {
        drawRuler(ctx, false);
      }
    }

    if (bottomRulerCanvas) {
      const ctx = bottomRulerCanvas.getContext('2d');
      if (!ctx) return;

      ctx.canvas.width = ctx.canvas.offsetWidth;
      ctx.canvas.height = rulerSize;
      ctx.canvas.style.bottom = `-${rulerSize}px`;
      ctx.canvas.style.left = `0`;

      if (showRuler) {
        drawRuler(ctx, true);
      }
    }
  }, [drawRuler, showRuler]);

  return <></>;
};

export default MeasuredWallpaperRulers;
